<template>
    <div
        v-observe-visibility="{ callback: marketsVisibilityChanged }"
        class="match JARVIS_MatchCard_View"
        v-bind:class="{ _live: match.isLive, _tournamentCenter: isShowTournamentCenterMatchInfo }"
        v-bind:data-match="gaDataMatch"
    >
        <MatchInfo
            v-if="!isShowTournamentCenterMatchInfo"
            v-bind:info="match"
            v-bind:marketPageType="match.marketPage"
        />
        <TournamentMatchInfo
            v-else
            v-bind:match="match"
            v-bind:marketPageType="match.marketPage"
            v-bind:displaySection="displaySection"
        />
        <div v-if="isBetBuilderEnabled" ref="tabContainerRef">
            <Tab
                v-if="isDisplayTab"
                v-model:value="marketTab"
                v-observe-visibility="{ callback: TabVisibilityChanged }"
                v-bind:tabList="marketTabs"
                class="matchTab"
            />
        </div>
        <BetBuilderMarkets
            v-if="isDisplayBetBuilder"
            v-bind:match="match"
            v-bind:isOpenMoreMarket="isOpenMoreMarket"
            v-bind:displaySection="displaySection"
            v-on:updateCount="updateBetBuilderMoreMarketCount"
        />
        <SboMarkets
            v-else
            v-bind:match="match"
            v-bind:isOpenMoreMarket="isOpenMoreMarket"
            v-bind:markets="markets"
            v-bind:displaySection="displaySection"
            v-bind:hasPreBuilt="hasPreBuilt"
            v-bind:recommendation="recommendation"
            v-bind:preBuiltEvent="preBuiltEvent"
            v-bind:preBuiltSelections="preBuiltSelections"
        />
        <MatchFunction
            v-bind:info="match"
            v-bind:selectedMarketTab="marketTab"
            v-bind:betBuilderMoreMarketCount="betBuilderMoreMarketCount"
            v-bind:isMoreMarketExpanded="isOpenMoreMarket"
            v-bind:marketCount="totalMarketCount"
            v-on:updateIsMoreMarketExpanded="updateIsOpenMoreMarket"
        />
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onBeforeUnmount, onMounted, ref, toRefs } from 'vue';
    import BetBuilderMarkets from '@/components/betBuilder/BetBuilderMarkets.vue';
    import Tab from '@/components/common/Tab.vue';
    import MatchFunction from '@/components/match/MatchFunction.vue';
    import SboMarkets from '@/components/match/SboMarkets.vue';
    import MatchInfo from '@/components/match/matchInfo/MatchInfo.vue';
    import TournamentMatchInfo from '@/components/tournamentCenter/TournamentMatchInfo.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { MarketTab } from '@/interface/enum';
    import { DisplaySection, SportType } from '@/interface/enum';
    import { SboMarketPointOrderContext } from '@/libraries/composables/SboMarketPointOrderContext';
    import { useMarketTab } from '@/libraries/composables/useMarketTab';
    import { useMoreMarketTabExpansion } from '@/libraries/composables/useMoreMarketTabExpansion';
    import { isShowTournamentCenterMatchInfo as _isShowTournamentCenterMatchInfo } from '@/libraries/composables/useZone';
    import { isSoccer } from '@/libraries/helpers/sportsHelper';
    import { i18n } from '@/libraries/i18n';
    import { useDynamicMarketCount } from '@/libraries/oddsApi/composables/useDynamicMarketCount';
    import { useMatchMarkets } from '@/libraries/oddsApi/composables/useMatchMarkets';
    import { useMatchPreBuiltRecommendation } from '@/libraries/recommendation/useMatchPreBuiltRecommendation';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import { useRootStore } from '@/stores/rootStore';

    export default defineComponent({
        name: 'Match',
        components: {
            MatchInfo,
            MatchFunction,
            SboMarkets,
            BetBuilderMarkets,
            Tab,
            TournamentMatchInfo,
        },
        emits: ['fetchOdds'],
        props: {
            match: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props, context) {
            const { match } = toRefs(props);

            SboMarketPointOrderContext.provide();

            const { liveMoreDefaultOpenLeagueIds, isBetBuilderEnabled, isJacktimeBetBuilderEnabled } = storeToRefs(useFeatureToggleStore());
            const { isDynamicMarketsEnabled: isDynamicMarketsAvailableForSport, isJacktimeBetBuilderSupportedLeague } = useFeatureToggleStore();

            const betBuilderMoreMarketCount = ref(0);
            const updateBetBuilderMoreMarketCount = (count: number) => {
                betBuilderMoreMarketCount.value = count;
            };

            const isBetBuilderVisible = ref(false);
            const isBetBuilderQueryEnable = computed(() => isBetBuilderEnabled.value && isBetBuilderVisible.value && (!isJacktimeBetBuilderSupportedLeague(match.value.leagueId) || isJacktimeBetBuilderEnabled.value));
            const { marketTab, marketTabs, isDisplayBetBuilder, setMarketTab } = useMarketTab(match.value.eventId, isBetBuilderQueryEnable, 'MatchCard');
            const { isOpenMoreMarket } = useMoreMarketTabExpansion(marketTab);
            const { isMixParlayMode } = storeToRefs(useRootStore());
            const isDisplayTab = computed(() => !isMixParlayMode.value && isSoccer(match.value.sportType) && isBetBuilderEnabled.value);

            const isMarketsVisible = ref(false);
            const isDynamicMarketAvailable = computed(() => isDynamicMarketsAvailableForSport(match.value.sportType) && !isMixParlayMode.value);
            const shouldLoadDynamicMarkets = computed(() => isDynamicMarketAvailable.value && isMarketsVisible.value);

            const isLive = computed(() => match.value.isLive);

            const { sortedMarkets: markets, refetch: refetchMarkets } = useMatchMarkets(
                match.value.eventId,
                isLive,
                match.value.sportType,
                match.value.leagueSize,
                match.value.leagueId,
                shouldLoadDynamicMarkets,
            );

            const { dynamicMarketCount } = useDynamicMarketCount(
                computed(() => match.value.eventId),
                isLive,
                isDynamicMarketAvailable,
            );

            const { hasPreBuilt, recommendation, event: preBuiltEvent, selections: preBuiltSelections } = useMatchPreBuiltRecommendation(
                match.value.eventId,
                isLive,
                isBetBuilderQueryEnable,
            );

            function getCurrentDynamicMarketCount() {
                if (!isDynamicMarketAvailable.value) return 0;
                if (dynamicMarketCount.value !== null) return dynamicMarketCount.value;
                return match.value.dynamicMarketCount ?? 0;
            }

            const totalMarketCount = computed(() => getCurrentDynamicMarketCount() + (hasPreBuilt.value ? 1 : 0));

            const gaDataMatch = computed(() => {
                switch (match.value.sportType) {
                    case SportType.Soccer:
                        return 'Football';
                    case SportType.Basketball:
                        return 'Basketball';
                    default:
                        return '';
                }
            });

            const isShowTournamentCenterMatchInfo = computed(() => _isShowTournamentCenterMatchInfo(match.value.leagueName));

            function updateIsOpenMoreMarket(isOpen: boolean) {
                isOpenMoreMarket.value = isOpen;
            }

            function setDefaultMoreMarketExpanded() {
                const isOpen = match.value.isLive && liveMoreDefaultOpenLeagueIds.value.includes(match.value.leagueId);
                updateIsOpenMoreMarket(isOpen);
            }

            function refreshMatch(matchId: number, forceCallApi: boolean) {
                if (matchId === match.value.eventId && forceCallApi) {
                    context.emit('fetchOdds');
                    refetchMarkets();
                }
            }

            const tabContainerRef = ref<HTMLElement | null>(null);
            const { headerTotalHeight, collapseHeight } = storeToRefs(useHeaderDisplayStore());

            function scrollMatchCardIntoView(el: HTMLElement | null) {
                if (!el) return;
                const targetScrollY = el.getBoundingClientRect().top + window.scrollY - (headerTotalHeight.value + collapseHeight.value);
                window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
            }

            async function changeMarketTab(matchId: number, tab: MarketTab) {
                if (matchId === match.value.eventId) {
                    setMarketTab(tab);
                    scrollMatchCardIntoView(tabContainerRef.value);
                }
            }

            onMounted(() => {
                window.eventEmitter.on('refreshMatch', refreshMatch);
                window.eventEmitter.on('changeMarketTab', changeMarketTab);
                setDefaultMoreMarketExpanded();
            });

            onBeforeUnmount(() => {
                window.eventEmitter.off('refreshMatch', refreshMatch);
                window.eventEmitter.off('changeMarketTab', changeMarketTab);
            });

            function TabVisibilityChanged(isVisible: boolean) {
                isBetBuilderVisible.value = isVisible;
            }

            function marketsVisibilityChanged(isVisible: boolean) {
                isMarketsVisible.value = isVisible;
            }

            return {
                i18n,
                isMixParlayMode,
                isOpenMoreMarket,
                markets,
                gaDataMatch,
                updateIsOpenMoreMarket,
                isDisplayBetBuilder,
                isBetBuilderEnabled,
                updateBetBuilderMoreMarketCount,
                betBuilderMoreMarketCount,
                marketTab,
                marketTabs,
                TabVisibilityChanged,
                isDisplayTab,
                marketsVisibilityChanged,
                isShowTournamentCenterMatchInfo,
                totalMarketCount,
                hasPreBuilt,
                recommendation,
                preBuiltEvent,
                preBuiltSelections,
                setMarketTab,
                tabContainerRef,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/match";
@import "@/scss/components/odd";
</style>
